import React, { useState, useEffect } from 'react';
import { enums, enumValue } from '~/util/enums';

export const externalReferralAttachmentError = (
    <>
        <div className="mb-3">
            Sorry, the document could not be uploaded. Please ensure a patient record is open in
            Zephyr and a Provider has been selected in the Referrals tab and then try again.
        </div>
        <div>If this problem persists, please contact HelpDesk@wellmed.net.</div>
    </>
);

export interface AlertContextType {
    alerts: AlertSetType[];
    set: (
        alert: string | JSX.Element,
        type: string,
        dismissOnChange?: number | null,
        banner?: number | boolean,
        id?: string | number
    ) => void;
    dismiss: (id: string | number) => void;
    advanceDismissOnChange: () => void;
    dismissAllBanners: () => void;
    initBanners: (banners: BannerType[]) => void;
    activateTriggeredBanner: (action: number | false) => void;
}

export type AlertSetType = {
    id: string | number;
    content: string | JSX.Element;
    type: string;
    banner: number | false;
    dismissOnChange: number | null;
};

export type BannerType = {
    id: number;
    content: string;
    settings: {
        header?: string;
        trigger?: number;
        type?: number;
    };
    style: number;
    start_date: string;
    end_date: string;
    created_at: string;
};

const AlertContext = React.createContext<AlertContextType | null>(null);

function AlertProvider({ ...props }: { children: JSX.Element }) {
    const [alerts, setAlerts] = useState<AlertSetType[]>([]);
    const [triggeredAlerts, setTriggeredAlerts] = useState<{ [key: number]: BannerType[] }>(
        Object.keys(enums(['banners', 'triggers'])).reduce((p, x) => ({ ...p, [x]: [] }), {})
    );

    function set(
        alert: string | JSX.Element,
        type: string,
        dismissOnChange?: number | null,
        banner?: number | boolean,
        id?: string | number
    ) {
        setAlerts((prev) => [
            ...prev,
            {
                id: id || Date.now(),
                content: alert,
                type,
                banner: banner === false || typeof banner === 'number' ? banner : 1,
                dismissOnChange: typeof dismissOnChange === 'undefined' ? 0 : dismissOnChange,
            },
        ]);
    }

    function dismiss(id: string | number) {
        setAlerts((prev) => prev.filter((x) => x.id !== id));
    }

    function advanceDismissOnChange() {
        // null values will persist on navigation change
        setAlerts((prev) =>
            prev
                .filter((x) => x.dismissOnChange === null || x.dismissOnChange > 0)
                .map((x) => ({
                    ...x,
                    dismissOnChange: x.dismissOnChange === null ? null : x.dismissOnChange - 1,
                }))
        );
    }

    // On logout, clear any banners that are visible right now, and reset the
    // stored triggered alerts which will be repopulated on next login
    const dismissAllBanners = React.useCallback(() => {
        setAlerts((prev) => prev.filter((x) => x.dismissOnChange !== null));
        setTriggeredAlerts(
            Object.keys(enums(['banners', 'triggers'])).reduce((p, x) => ({ ...p, [x]: [] }), {})
        );
    }, []);

    const initBanners = React.useCallback((banners: BannerType[]) => {
        if (!banners) return;
        banners.forEach((banner) => {
            if (banner?.settings?.trigger) {
                setTriggeredAlerts((prev) => ({
                    ...prev,
                    [banner.settings.trigger as number]: [
                        ...prev[banner.settings.trigger as number],
                        banner,
                    ],
                }));
            } else {
                set(
                    banner.content,
                    banner?.settings?.type !== enumValue(['banners', 'type', 'Modal'])
                        ? enums(['banners', 'style', banner.style]).toLowerCase()
                        : banner?.settings?.header,
                    null,
                    banner?.settings?.type,
                    `announcement-${banner.id}`
                );
            }
        });
    }, []);

    function activateTriggeredBanner(action: number | false) {
        if (!action) return;
        if (!triggeredAlerts?.[action] || triggeredAlerts[action].length === 0) return;
        triggeredAlerts[action].forEach((banner) => {
            set(
                banner.content,
                banner?.settings?.type !== enumValue(['banners', 'type', 'Modal'])
                    ? enums(['banners', 'style', banner.style]).toLowerCase()
                    : banner?.settings?.header,
                null,
                banner?.settings?.type,
                `announcement-${banner.id}`
            );
        });
        setTriggeredAlerts((prev) => ({ ...prev, [action]: [] }));
    }

    useEffect(() => {
        if (window.app.user?.banners) {
            initBanners(window.app.user.banners);
        }
    }, [initBanners]);

    useEffect(() => {
        function pageListener(request: MessageEvent<ExternalAttachmentEventType>) {
            if (request.source !== window || request.origin !== window.location.origin) return;
            if (request.data.method === 'documentPrintError') {
                const printErrorMsg =
                    request.data.data.destination === 'zephyr-referral' ? (
                        externalReferralAttachmentError
                    ) : (
                        <>
                            <div className="mb-3">
                                Sorry, the document could not be uploaded. Please ensure a patient
                                record is open in Zephyr and then try again.
                            </div>
                            <div>
                                If this problem persists, please contact HelpDesk@wellmed.net.
                            </div>
                        </>
                    );
                set(printErrorMsg, 'Document Upload Error', 0, 0);
            }
        }
        window.addEventListener('message', pageListener);
        return () => {
            window.removeEventListener('message', pageListener);
        };
    }, []);

    const methods = {
        alerts,
        set,
        dismiss,
        advanceDismissOnChange,
        dismissAllBanners,
        initBanners,
        activateTriggeredBanner,
    };

    return <AlertContext.Provider value={methods} {...props} />;
}

export { AlertContext, AlertProvider };
